import ResizeObserver from 'resize-observer-polyfill';

import { mq, lodash } from '../helpers';

import Platform from '../constants/platform';

/* 	Resize Controller

	1) Provides unified window / document / manual resize reference
	2) Separates read / write operations via update() and render() callbacks

*/

class ResizeController {

	coords = {
		window: {
			width: 0,
			height: 0,
			last: {
				width: 0,
				height: 0
			}
		}
	};

	el = {
		sections: null
	};

	observers = {
		resizeBody: null,
		resizeSections: null
	};

	throttled = {
		measure: null
	};

	states = {
		last: {
			platform: Platform.DESKTOP
		},
		platform: Platform.DESKTOP
	};

	subscribers = [];

	initialize() {
		this.throttled.measure = lodash.throttle(this._measureNow.bind(this), 500, { leading: false, trailing: true });
		this.addEventListeners();
		this.measure(true);
	}

	// listeners

	addEventListeners() {
		this.observers.resizeBody = new ResizeObserver(this.#onResizeObserved);
		this.observers.resizeBody.observe(document.body);
	}

	removeEventListeners() {
		this.observers.resizeBody.disconnect();
		this.observers.resizeBody = null;
	}

	// observe individual sections (necessary for smooth scrolling)

	observeSections(sections) {
		this.disconnectSections();
		this.observers.resizeSections = new ResizeObserver(this.#onResizeObserved);
		for (let i = 0; i < sections.length; i++) {
			const $section = sections[i];
			this.observers.resizeSections.observe($section);
		}
	}

	disconnectSections() {
		if (this.observers.resizeSections) {
			this.observers.resizeSections.disconnect();
			this.observers.resizeSections = null;
		}
	}

	// listener methods

	#onResizeObserved = () => {
		this.measure();
	};

	//

	measure(instant = false) {
		if (instant) {
			this._measureNow();
		} else {
			this.throttled.measure();
		}
	}

	_measureNow() {
		this.coords.window.last.width = this.coords.window.width;
		this.coords.window.last.height = this.coords.window.height;

		this.coords.window.width = window.innerWidth;
		this.coords.window.height = window.innerHeight;

		this.update();
		this.render();
	}

	// add / remove subscribers

	set(obj) {
		obj.updated = false;
		this.subscribers.push(obj);
		return this.subscribers.length - 1;
	}

	clear(index) {
		this.subscribers[index] = null;
	}

	// subscribe new object for only one tick cycle

	one(obj) {
		const index = this.set(obj);
		this.subscribers[index].die = true;
	}

	// update (read cycle)

	update() {
		this.states.last.platform = this.states.platform;
		this.states.platform = this.platform;

		for (let i = 0, l = this.subscribers.length; i < l; ++i) {
			const subscriber = this.subscribers[i];
			if (subscriber && subscriber.update()) subscriber.updated = true;
		}
	}

	// render (write cycle)

	render() {
		for (let i = 0, l = this.subscribers.length; i < l; ++i) {
			const subscriber = this.subscribers[i];
			// will only fire if subscriber has returned true from previous update() cycle
			if (subscriber && subscriber.updated) {
				subscriber.updated = false;
				subscriber.render();
				if (subscriber.die) this.clear(i);
			}
		}
	}

	// get device platform based on breakpoint

	get platform() {
		let platform = Platform.DESKTOP;

		if (mq('medium')) {
			platform = Platform.DESKTOP;
		} else if (mq('small') && !mq('medium')) {
			platform = Platform.TABLET;
		} else {
			platform = Platform.MOBILE;
		}

		return platform;
	}

	// determine if platform just changed

	get isPlatformChange() {
		return this.states.platform !== this.states.last.platform;
	}

	get isViewportChange() {
		return (this.coords.window.width !== this.coords.window.last.width) || (this.coords.window.height !== this.coords.window.last.height);
	}

}

export default new ResizeController();
